import { useEffect, useState, useCallback } from "react";
import { osApi, formatINR, statusColor } from "./osApi";
import { toast } from "sonner";
import OSLeadDrawer from "./OSLeadDrawer";
import { GripVertical } from "lucide-react";

export default function OSPipeline() {
  const [data, setData] = useState(null);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setData(await osApi.pipeline()); } catch (e) { toast.error("Failed to load pipeline"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragStart = (e, lead) => {
    setDrag(lead);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, stage) => { e.preventDefault(); setOver(stage); };
  const onDrop = async (e, stage) => {
    e.preventDefault();
    setOver(null);
    if (!drag || drag.stage === stage) return setDrag(null);
    // Optimistic
    setData(prev => {
      const leads = { ...prev.leads };
      leads[drag.stage] = leads[drag.stage].filter(l => l.id !== drag.id);
      leads[stage] = [{ ...drag, stage }, ...leads[stage]];
      return { ...prev, leads };
    });
    try {
      await osApi.moveStage(drag.id, stage);
      toast.success(`Moved to ${stage}`);
    } catch (err) {
      toast.error("Failed to move");
      load();
    } finally { setDrag(null); }
  };

  return (
    <div className="space-y-5" data-testid="os-pipeline-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Pipeline</h1>
        <p className="text-sm mt-1" style={{color:"var(--os-text-muted)"}}>Drag leads across the 10 stages of your sales flow.</p>
      </div>
      {loading && <div className="os-card p-8 text-center" style={{color:"var(--os-text-muted)"}}>Loading pipeline…</div>}
      {data && (
        <div className="os-kanban" data-testid="os-kanban">
          {data.stages.map(stage => {
            const cards = data.leads[stage] || [];
            const total = cards.reduce((s, l) => s + (l.deal_value || l.budget || 0), 0);
            return (
              <div key={stage}
                   className={`os-kanban-col ${over === stage ? "drop-over" : ""}`}
                   onDragOver={(e) => onDragOver(e, stage)}
                   onDrop={(e) => onDrop(e, stage)}
                   onDragLeave={() => setOver(null)}
                   data-testid={`os-kanban-col-${stage.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="os-kanban-col-header">
                  <div className="font-medium text-sm">{stage}</div>
                  <div className="flex items-center gap-2">
                    <span className="os-badge os-badge-blue">{cards.length}</span>
                  </div>
                </div>
                <div className="px-3 py-2 text-[11px] os-mono" style={{color:"var(--os-text-muted)", borderBottom:"1px solid var(--os-border)"}}>
                  {formatINR(total)}
                </div>
                <div className="os-kanban-col-body">
                  {cards.map(l => (
                    <div key={l.id}
                         className={`os-kanban-card ${drag?.id === l.id ? "dragging" : ""}`}
                         draggable
                         onDragStart={(e) => onDragStart(e, l)}
                         onClick={() => setSelected(l.id)}
                         data-testid={`os-kanban-card-${l.lead_id}`}>
                      <div className="flex justify-between items-start">
                        <div className="text-[10px] os-mono" style={{color:"var(--os-text-dim)"}}>{l.lead_id}</div>
                        <span className={`os-badge ${statusColor(l.lead_status)}`}>{l.lead_status}</span>
                      </div>
                      <div className="text-sm font-medium mt-1">{l.customer_name}</div>
                      <div className="text-[11px]" style={{color:"var(--os-text-muted)"}}>{l.location || "—"}</div>
                      <div className="mt-2 flex justify-between items-center">
                        <div className="os-mono text-xs">{formatINR(l.budget)}</div>
                        <div className="text-[10px]" style={{color:"var(--os-text-dim)"}}>{l.assigned_agent_name || "Unassigned"}</div>
                      </div>
                    </div>
                  ))}
                  {cards.length === 0 && (
                    <div className="text-center py-6 text-xs" style={{color:"var(--os-text-dim)"}}>No leads</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {selected && <OSLeadDrawer leadId={selected} onClose={() => setSelected(null)} onUpdate={load} />}
    </div>
  );
}
